import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TagIcon, XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';

// Redux
import { setOrderDataAction } from '../../../redux/Actions/CreateOrderActions';
import { getPromotionsApi } from '../../../redux/Reducers/commonApis';

// Utils
import { amountCheckFn } from '../../../utils/commonUtil';

// Components
import { Spinner } from './ui';
import { SECTION_IDS, formatSlot, isOk, pickupDateOf, providerName, scrollToSection } from './helpers';
import { paymentLabel } from './PaymentSection';

const tenant = import.meta.env.VITE_TENANT_NAME;
const TOS_URL = tenant === 'laundrycare' ? 'https://www.laundrycare.biz/tos/' : 'https://www.pushlaundry.com/tos/';
const TIP_PRESETS = [3, 5, 10];

function DetailRow({ label, value, target }) {
  return (
    <div className='flex gap-3 py-1.5 text-sm'>
      <dt className='w-[70px] shrink-0 text-slate-500'>{label}</dt>
      <dd className='min-w-0 flex-1'>
        {value ? (
          <span className='font-medium text-slate-800'>{value}</span>
        ) : (
          <button
            type='button'
            className='text-left text-amber-600 hover:underline'
            onClick={() => scrollToSection(target)}
          >
            Not selected
          </button>
        )}
      </dd>
    </div>
  );
}

function PriceRow({ label, value, className = '' }) {
  return (
    <div className={`flex justify-between text-sm ${className}`}>
      <span className='text-slate-500'>{label}</span>
      <span className='text-slate-800'>{value}</span>
    </div>
  );
}

function PromoField({ promoError }) {
  const dispatch = useDispatch();
  const { orderPromoCode, orderServiceAddress } = useSelector((s) => s.createNewOrder);
  const [code, setCode] = useState('');
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    if (!orderServiceAddress?.id) return;
    getPromotionsApi(orderServiceAddress.id).then((res) => {
      if (!isOk(res)) return;
      const now = new Date();
      setPromos((res?.data || []).filter((p) => !p?.end_date || new Date(p.end_date) >= now));
    });
  }, [orderServiceAddress?.id]);

  const apply = (c) => {
    const value = (c ?? code).trim();
    if (value) dispatch(setOrderDataAction({ orderPromoCode: value }));
  };

  if (orderPromoCode)
    return (
      <div className='flex items-center justify-between rounded-md bg-green-50 px-3 py-2'>
        <span className='flex items-center gap-1.5 text-xs font-semibold text-green-700'>
          <TagIcon className='h-4 w-4' />
          {orderPromoCode} applied
        </span>
        <button
          type='button'
          aria-label='Remove promo code'
          className='rounded p-0.5 text-green-700 hover:bg-green-100'
          onClick={() => {
            setCode('');
            dispatch(setOrderDataAction({ orderPromoCode: null }));
          }}
        >
          <XMarkIcon className='h-4 w-4' />
        </button>
      </div>
    );

  return (
    <div>
      <div className='flex gap-2'>
        <input
          className='focus:border-client-300 focus:ring-client-300 h-9 min-w-0 flex-1 rounded-md border border-slate-200 px-3 text-sm'
          placeholder='Promo code'
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()}
        />
        <button
          type='button'
          disabled={!code.trim()}
          onClick={() => apply()}
          className='h-9 rounded-md border border-slate-200 px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-40'
        >
          Apply
        </button>
      </div>
      {promoError && <p className='mt-1 text-xs text-red-600'>{promoError}</p>}
      {promos.length > 0 && (
        <div className='mt-2 flex flex-wrap gap-1.5'>
          {promos.map((p) => (
            <button
              key={p.id}
              type='button'
              title={p.message || ''}
              onClick={() => apply(p.code)}
              className='flex items-center gap-1 rounded-md border border-dashed border-green-300 bg-green-50/60 px-2.5 py-1 text-[11px] font-semibold text-green-700 transition hover:border-green-500'
            >
              <TagIcon className='h-3 w-3' />
              {p.code} · {p.type === 'PERCENTAGE_OFF' ? `${p.type_amount}% off` : `$${p.type_amount} off`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TipField() {
  const dispatch = useDispatch();
  const { final_tip, orderProvider } = useSelector((s) => s.createNewOrder);
  const isPreset = final_tip == null || TIP_PRESETS.includes(final_tip);
  const [customOpen, setCustomOpen] = useState(!isPreset);
  const [custom, setCustom] = useState(isPreset ? '' : String(final_tip));
  const [error, setError] = useState(false);

  const setTip = (v) => {
    setCustomOpen(false);
    setError(false);
    dispatch(setOrderDataAction({ final_tip: v }));
  };
  const saveCustom = () => {
    if (custom === '') return setTip(null);
    const n = +custom;
    if (isNaN(n) || n < 1 || n > 500) return setError(true);
    setError(false);
    dispatch(setOrderDataAction({ final_tip: n }));
  };

  const pill = (active) =>
    `h-8 rounded-md border text-xs font-semibold transition ${
      active ? 'border-client-300 bg-client-300 text-white' : 'border-slate-200 text-slate-700 hover:border-slate-300'
    }`;

  return (
    <div>
      <p className='mb-1.5 text-xs font-semibold text-slate-600'>
        Tip {orderProvider?.id ? orderProvider.first_name : 'your provider'}
        <span className='font-normal text-slate-400'> · optional</span>
      </p>
      <div className='grid grid-cols-5 gap-1.5'>
        <button type='button' className={pill(final_tip == null && !customOpen)} onClick={() => setTip(null)}>
          None
        </button>
        {TIP_PRESETS.map((v) => (
          <button key={v} type='button' className={pill(final_tip === v && !customOpen)} onClick={() => setTip(v)}>
            ${v}
          </button>
        ))}
        <button type='button' className={pill(customOpen)} onClick={() => setCustomOpen(true)}>
          Other
        </button>
      </div>
      {customOpen && (
        <div className='mt-2'>
          <div className='relative'>
            <span className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400'>
              $
            </span>
            <input
              autoFocus
              type='number'
              min={1}
              max={500}
              className={`focus:border-client-300 focus:ring-client-300 h-9 w-full rounded-md border pl-6 pr-3 text-sm ${
                error ? 'border-red-300' : 'border-slate-200'
              }`}
              placeholder='Enter amount'
              value={custom}
              onChange={(e) => {
                setCustom(e.target.value);
                setError(false);
              }}
              onBlur={saveCustom}
              onKeyDown={(e) => e.key === 'Enter' && saveCustom()}
            />
          </div>
          {error && <p className='mt-1 text-xs text-red-600'>Tip must be between $1 and $500.</p>}
        </div>
      )}
    </div>
  );
}

export default function SummaryPanel({ estimate, estimateLoading, promoError, missing, onPlace, placing, placeError }) {
  const {
    orderType,
    orderRepeatFrequency,
    orderServiceAddress,
    orderProvider,
    orderTimeSlots,
    orderPaymentDetails,
    selectedServices,
  } = useSelector((s) => s.createNewOrder);
  const tz = orderServiceAddress?.time_zone;
  const isSub = orderType === 'subscription';
  const hasItems = selectedServices?.length > 0;
  const ready = missing.length === 0;
  const [agreedToTos, setAgreedToTos] = useState(false);

  return (
    <aside
      id={SECTION_IDS.summary}
      className='scroll-mt-4 rounded-lg border border-slate-200 bg-white shadow-sm lg:flex lg:max-h-[calc(100vh-7rem)] lg:flex-col'
    >
      <div className='flex items-center justify-between border-b border-slate-100 px-5 py-3.5'>
        <h2 className='text-[15px] font-semibold text-slate-900'>Order summary</h2>
        <span className='bg-client-iconbg text-client-300 rounded-md px-2.5 py-0.5 text-[11px] font-semibold'>
          {isSub ? `Repeats ${orderRepeatFrequency?.name?.toLowerCase() || ''}` : 'One-time'}
        </span>
      </div>

      <div className='space-y-4 px-5 py-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto'>
        <dl className='divide-y divide-slate-100'>
          <DetailRow label='Address' value={orderServiceAddress?.name} target={SECTION_IDS.address} />
          <DetailRow
            label={isSub ? '1st pickup' : 'Pickup'}
            value={formatSlot(
              pickupDateOf(orderTimeSlots),
              orderTimeSlots?.pickup_start_time,
              orderTimeSlots?.pickup_end_time,
              tz
            )}
            target={SECTION_IDS.schedule}
          />
          <DetailRow
            label='Delivery'
            value={formatSlot(
              orderTimeSlots?.dropoff_date,
              orderTimeSlots?.dropoff_start_time,
              orderTimeSlots?.dropoff_end_time,
              tz
            )}
            target={SECTION_IDS.schedule}
          />
          <DetailRow label='Provider' value={providerName(orderProvider)} />
          <DetailRow label='Payment' value={paymentLabel(orderPaymentDetails)} target={SECTION_IDS.payment} />
        </dl>

        {/* Pricing */}
        {hasItems && (
          <div className={`space-y-1.5 border-t border-slate-100 pt-3 ${estimateLoading ? 'opacity-60' : ''}`}>
            {(estimate?.items || []).map((s, i) => (
              <PriceRow key={i} label={`${s.name} × ${s.quantity}`} value={amountCheckFn(s.client_fee)} />
            ))}
            {estimate?.items?.length > 0 && <div className='border-t border-dashed border-slate-200 pt-1.5' />}
            <PriceRow label='Subtotal' value={amountCheckFn(estimate?.subtotal)} />
            {estimate?.order_minimum_difference > 0 && (
              <PriceRow
                label='Order minimum adjustment'
                value={`+${amountCheckFn(estimate.order_minimum_difference)}`}
              />
            )}
            {estimate?.promo_code_discount > 0 && (
              <PriceRow
                label='Promo'
                value={<span className='text-green-600'>−{amountCheckFn(estimate.promo_code_discount)}</span>}
              />
            )}
            {estimate?.priority_fee > 0 && (
              <PriceRow label='Express delivery' value={`+${amountCheckFn(estimate.priority_fee)}`} />
            )}
            {estimate?.tip > 0 && <PriceRow label='Tip' value={amountCheckFn(estimate.tip)} />}
            <PriceRow label='Service fee' value={amountCheckFn(estimate?.service_fee)} />
            {estimate?.order_minimum > 0 && (
              <p className='pt-1 text-[11px] text-slate-400'>${estimate.order_minimum} order minimum applies.</p>
            )}
          </div>
        )}

        {hasItems && orderServiceAddress && (
          <div className='space-y-4 border-t border-slate-100 pt-4'>
            <PromoField promoError={promoError} />
            <TipField />
          </div>
        )}

        <details className='group rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600'>
          <summary className='flex cursor-pointer list-none items-center gap-1.5 font-semibold text-slate-700'>
            <InformationCircleIcon className='h-4 w-4 text-slate-400' />
            Good to know
          </summary>
          <ul className='mt-2 list-disc space-y-1 pl-5'>
            <li>
              {tenant === 'laundrycare'
                ? 'Use 13-gal non-stretch trash bags or our reusable bags.'
                : 'Put dirty laundry in trash bags or another closeable bag.'}
            </li>
            <li>
              All items are machine-washed and tumble-dried (no dry cleaning). Stain removal isn't guaranteed, and
              air-dry requests may return slightly damp.
            </li>
            <li>
              A temporary hold for the estimated total is placed on your card; the final charge is captured after
              delivery.
            </li>
          </ul>
        </details>
      </div>

      {/* Footer: total + action, always visible */}
      <div className='border-t border-slate-100 px-5 py-4'>
        <div className='mb-3 flex items-end justify-between'>
          <span className='text-sm font-semibold text-slate-700'>Estimated total</span>
          <span className='flex items-center gap-2 text-xl font-bold text-slate-900'>
            {estimateLoading && <Spinner />}
            {hasItems ? amountCheckFn(estimate?.estimated_total_cost) : '$0.00'}
          </span>
        </div>
        <label className='mb-3 flex cursor-pointer items-start gap-2 text-xs text-slate-600'>
          <input
            type='checkbox'
            checked={agreedToTos}
            onChange={(e) => setAgreedToTos(e.target.checked)}
            className='text-client-300 focus:ring-client-300 mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300'
          />
          <span>
            Confirmation is agreement to our{' '}
            <a
              href={TOS_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='text-client-300 font-semibold hover:underline'
            >
              Terms of Service
            </a>
          </span>
        </label>
        <button
          type='button'
          disabled={!ready || !agreedToTos || placing || estimateLoading}
          onClick={onPlace}
          className='bg-client-300 hover:bg-client-600 w-full rounded-md py-3 text-sm font-bold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500 disabled:shadow-none'
        >
          {placing ? 'Placing order…' : isSub ? 'Start subscription' : 'Place order'}
        </button>
        {!ready && (
          <p className='mt-2 text-xs leading-relaxed text-slate-500'>
            Still needed:{' '}
            {missing.map((m, i) => (
              <React.Fragment key={m.label}>
                <button
                  type='button'
                  className='text-client-300 font-semibold hover:underline'
                  onClick={() => scrollToSection(m.id)}
                >
                  {m.label}
                </button>
                {i < missing.length - 1 && ', '}
              </React.Fragment>
            ))}
          </p>
        )}
        {placeError && <p className='mt-2 rounded-md bg-red-50 px-3 py-2 text-xs text-red-600'>{placeError}</p>}
      </div>
    </aside>
  );
}
